import type {
  OnboardingSurveyAnswer,
  OnboardingSurveyQuestionId,
} from "@mirage/service-onboarding-surveys/service/types";
import { useEffect, useState } from "react";
import { isLargeCompany, useOnboardingSurveys } from "./useOnboardingSurveys";
import { useSelfServeProfilingSurveyName } from "./useSelfServeProfilingSurveyName";

const ANSWERED_QUESTIONS = [
  "COMPANY_SIZE_V1",
  "INDUSTRY_V1",
  "KIND_OF_WORK_V1",
];

const isValidAnswer = (value: string | undefined): boolean => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

export const useSelfServeFssSurveyResponses = (
  isAdmin: boolean,
  canProvisionDashFreemiumTeam: boolean,
  fssSurveyResponses: OnboardingSurveyAnswer[],
) => {
  const [answeredProfilingQuestions, setAnsweredProfilingQuestions] =
    useState<boolean>(false);

  const { surveyName } = useSelfServeProfilingSurveyName(
    isAdmin,
    canProvisionDashFreemiumTeam,
  );

  const { questions, submitSurvey, surveyResponses } = useOnboardingSurveys(
    surveyName,
    false,
  );

  useEffect(() => {
    const maybeSubmitMissingAnswers = async () => {
      if (!questions.length) return;

      const answeredIds = new Set(
        surveyResponses
          .filter((r) => isValidAnswer(r.surveyAnswer))
          .map((r) => r.surveyQuestionId),
      );

      const allAnswered = questions.every((q) => answeredIds.has(q.questionId));
      if (allAnswered) {
        setAnsweredProfilingQuestions(true);
        return;
      }

      if (!fssSurveyResponses?.length || isLargeCompany(fssSurveyResponses))
        return;

      const missingAnswers = questions
        .filter((q) => !answeredIds.has(q.questionId))
        .map((q) => q.questionId);

      const fssAnswersToSubmit = fssSurveyResponses.filter((r) =>
        missingAnswers.includes(
          r.surveyQuestionId as OnboardingSurveyQuestionId,
        ),
      );
      if (!fssAnswersToSubmit.length) return;

      await submitSurvey(fssAnswersToSubmit);

      const updatedAnsweredIds = new Set([
        ...answeredIds,
        ...fssAnswersToSubmit.map((r) => r.surveyQuestionId),
      ]);

      if (ANSWERED_QUESTIONS.every((id) => updatedAnsweredIds.has(id))) {
        setAnsweredProfilingQuestions(true);
      }
    };

    void maybeSubmitMissingAnswers();
  }, [fssSurveyResponses, questions, surveyResponses, submitSurvey]);

  return { surveyResponses, answeredProfilingQuestions };
};
